import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  Pagination,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconArchive, IconDotsVertical, IconEar, IconInbox, IconPlayerPlay, IconTrash } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { useOrg } from '@/hooks/use-org';
import { OrganizationPhoneNumbersSubscription } from './-components/call-graphql';
import { formatDateTime, formatDuration, formatPhone, timeAgo } from './-components/format';
import { StatTile } from './-components/stat-cards';
import { NewVoicemailCountSubscription } from './-components/graphql';
import {
  UpdateVoicemailStatusMutation,
  VoicemailsCountSubscription,
  VoicemailsSubscription,
} from './-components/voicemail-graphql';

const PAGE_SIZE = 50;

const STATUS_COLORS: Record<string, string> = {
  new: 'blue',
  listened: 'gray',
  archived: 'dimmed',
};

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Voicemails' },
  { value: 'new', label: 'New' },
  { value: 'listened', label: 'Listened' },
  { value: 'archived', label: 'Archived' },
];

type VoicemailRow = {
  id: string;
  fromNumber: string;
  phoneNumberLabel: string | null;
  durationSeconds: number;
  status: string;
  createdAt: string;
  flowName: string | null;
  hasRecording: boolean;
};

export const Route = createFileRoute('/_authed/voicemails')({
  component: VoicemailsPage,
});

function PlaybackModal({
  voicemail,
  playbackUrl,
  playbackError,
  isLoading,
  onClose,
}: {
  voicemail: VoicemailRow | null;
  playbackUrl: string | null;
  playbackError: string | null;
  isLoading: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      opened={voicemail !== null}
      onClose={onClose}
      title={voicemail ? `Voicemail from ${formatPhone(voicemail.fromNumber)}` : 'Voicemail'}
      size="lg"
    >
      {voicemail && (
        <Stack gap="md">
          <Group gap="xs">
            <Badge variant="light" color={STATUS_COLORS[voicemail.status]}>
              {voicemail.status}
            </Badge>
            <Badge variant="light" color="grape">
              {voicemail.flowName ?? 'Unknown flow'}
            </Badge>
            <Text size="sm" c="dimmed">
              {formatDuration(voicemail.durationSeconds)} · {formatDateTime(voicemail.createdAt)}
            </Text>
          </Group>

          {isLoading && (
            <Center py="xl">
              <Loader size="lg" />
            </Center>
          )}

          <ErrorAlert message={playbackError} />

          {playbackUrl && <audio key={playbackUrl} src={playbackUrl} controls autoPlay style={{ width: '100%' }} />}
        </Stack>
      )}
    </Modal>
  );
}

function Voicemails({ organizationId }: { organizationId: string }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [phoneNumberFilter, setPhoneNumberFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<VoicemailRow | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [{ data: newCountData }] = useSubscription({
    query: NewVoicemailCountSubscription,
    variables: { organizationId },
  });
  const [{ data: phoneData }] = useSubscription({
    query: OrganizationPhoneNumbersSubscription,
    variables: { organizationId },
  });
  const where = {
    organizationId: { _eq: organizationId },
    status: statusFilter === 'all' ? undefined : { _eq: statusFilter },
    phoneNumberId: phoneNumberFilter ? { _eq: phoneNumberFilter } : undefined,
  };

  const [{ data }] = useSubscription({
    query: VoicemailsSubscription,
    variables: { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE, where },
  });
  const [{ data: countData }] = useSubscription({ query: VoicemailsCountSubscription, variables: { where } });

  const [, updateStatus] = useMutation(UpdateVoicemailStatusMutation);

  const rows: VoicemailRow[] = (data?.voicemails ?? []).map((vm) => ({
    id: vm.id,
    fromNumber: vm.fromNumber,
    phoneNumberLabel: vm.phoneNumber?.friendlyName ?? (vm.phoneNumber ? formatPhone(vm.phoneNumber.phoneNumber) : null),
    durationSeconds: vm.durationSeconds,
    status: vm.status,
    createdAt: vm.createdAt,
    flowName: vm.flow?.name ?? null,
    hasRecording: !!(vm.s3Key || vm.recordingUrl || vm.publicRecordingUrl),
  }));

  const total = countData?.voicemailsAggregate.aggregate?.count ?? 0;

  const openPlayer = async (voicemail: VoicemailRow) => {
    setSelected(voicemail);
    setPlaybackUrl(null);
    setPlaybackError(null);
    setLoadingId(voicemail.id);

    const { data: link, error } = await api.voicemails({ id: voicemail.id })['access-link'].get();

    setLoadingId(null);

    if (error) return setPlaybackError(getErrorMessage(error, 'Recording not available'));

    setPlaybackUrl(link.url);

    if (voicemail.status === 'new') await updateStatus({ id: voicemail.id, status: 'listened' });
  };

  const closeModal = () => {
    setSelected(null);
    setPlaybackUrl(null);
    setPlaybackError(null);
    setLoadingId(null);
  };

  const removeVoicemail = async (id: string) => {
    setActionError(null);

    const { error } = await api.voicemails({ id }).delete();

    if (error) setActionError(getErrorMessage(error, 'Failed to delete voicemail'));
  };

  if (!data || !countData || !newCountData) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <>
      <Stack gap="xl">
        <div>
          <Title order={2}>Voicemails</Title>
          <Text size="sm" c="dimmed" mt="2xs">
            Listen to and manage voicemail messages left by callers.
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <StatTile
            label="New Voicemails"
            value={newCountData.voicemailsAggregate.aggregate?.count ?? 0}
            icon={<IconInbox size={24} />}
            color="blue"
          />
          <StatTile label="Total Voicemails" value={total} icon={<IconInbox size={24} />} color="grape" />
        </SimpleGrid>

        <Group gap="md">
          <Select
            placeholder="Filter by status"
            data={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(v) => {
              setPage(1);
              setStatusFilter(v ?? 'all');
            }}
            w={200}
            clearable={false}
          />
          <Select
            placeholder="All phone numbers"
            data={(phoneData?.phoneNumbers ?? []).map((p) => ({
              value: p.id,
              label: p.friendlyName ? `${p.friendlyName} (${formatPhone(p.phoneNumber)})` : formatPhone(p.phoneNumber),
            }))}
            value={phoneNumberFilter}
            onChange={(v) => {
              setPage(1);
              setPhoneNumberFilter(v);
            }}
            w={280}
            clearable
            searchable
          />
        </Group>

        <ErrorAlert message={actionError} />

        {rows.length === 0 ? (
          <Paper p="3xl" radius="lg" withBorder>
            <Stack align="center" gap="md">
              <ThemeIcon size={52} radius="xl" variant="light" color="gray">
                <IconInbox size={26} />
              </ThemeIcon>
              <div style={{ textAlign: 'center' }}>
                <Text fw={600}>No voicemails</Text>
                <Text size="sm" c="dimmed" mt="xs">
                  Voicemail messages will appear here when callers leave them.
                </Text>
              </div>
            </Stack>
          </Paper>
        ) : (
          <Paper p={0} radius="lg" style={{ overflow: 'hidden' }} withBorder>
            <Table.ScrollContainer minWidth={760}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Caller</Table.Th>
                    <Table.Th>Phone Number</Table.Th>
                    <Table.Th>Flow</Table.Th>
                    <Table.Th>Duration</Table.Th>
                    <Table.Th>Time</Table.Th>
                    <Table.Th w={92} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((row) => (
                    <Table.Tr
                      key={row.id}
                      style={{
                        fontWeight: row.status === 'new' ? 600 : undefined,
                        opacity: row.status === 'archived' ? 0.6 : undefined,
                      }}
                    >
                      <Table.Td>
                        <Badge size="sm" variant="light" color={STATUS_COLORS[row.status]}>
                          {row.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{formatPhone(row.fromNumber)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={row.phoneNumberLabel ? undefined : 'dimmed'}>
                          {row.phoneNumberLabel ?? 'Unknown'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={row.flowName ? undefined : 'dimmed'}>
                          {row.flowName ?? 'Unknown'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{formatDuration(row.durationSeconds)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={formatDateTime(row.createdAt)}>
                          <Text size="sm">{timeAgo(row.createdAt)}</Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6} justify="flex-end">
                          <Tooltip label="Play">
                            <ActionIcon
                              variant="light"
                              color="indigo"
                              onClick={() => void openPlayer(row)}
                              disabled={!row.hasRecording || loadingId === row.id}
                            >
                              {loadingId === row.id ? <Loader size={14} /> : <IconPlayerPlay size={14} />}
                            </ActionIcon>
                          </Tooltip>
                          <Menu position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon variant="subtle" color="gray">
                                <IconDotsVertical size={14} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              {row.status === 'new' && (
                                <Menu.Item
                                  leftSection={<IconEar size={14} />}
                                  onClick={() => void updateStatus({ id: row.id, status: 'listened' })}
                                >
                                  Mark as Listened
                                </Menu.Item>
                              )}
                              {row.status !== 'archived' && (
                                <Menu.Item
                                  leftSection={<IconArchive size={14} />}
                                  onClick={() => void updateStatus({ id: row.id, status: 'archived' })}
                                >
                                  Archive
                                </Menu.Item>
                              )}
                              <Menu.Item
                                leftSection={<IconTrash size={14} />}
                                color="red"
                                onClick={() => void removeVoicemail(row.id)}
                              >
                                Delete
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            {total > PAGE_SIZE && (
              <Group justify="center" p="md">
                <Pagination value={page} onChange={setPage} total={Math.ceil(total / PAGE_SIZE)} size="sm" />
              </Group>
            )}
          </Paper>
        )}
      </Stack>

      <PlaybackModal
        voicemail={selected}
        playbackUrl={playbackUrl}
        playbackError={playbackError}
        isLoading={loadingId !== null && selected !== null}
        onClose={closeModal}
      />
    </>
  );
}

function VoicemailsPage() {
  const { organizationId, isLoading } = useOrg();

  if (isLoading) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId) {
    return (
      <Center h="30vh">
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  return <Voicemails organizationId={organizationId} />;
}
